import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { Input } from '@/shared/components/ui/input';
import { cn } from '@/core/lib/utils';
import { ChecklistCardHeader } from './ChecklistCardHeader';
import { ChecklistItemRow } from './ChecklistItemRow';
import { createChecklistItemTitleSchema } from '../schemas';
import type { Checklist } from '../types';

interface ChecklistCardProps {
  checklist: Checklist;
  canConvertToSubtask: boolean;
  onAddItem: (checklistId: string, title: string) => void;
  onRenameChecklist: (checklistId: string, name: string) => void;
  onDeleteChecklist: (checklist: Checklist) => void;
  onToggleItem: (itemId: string, isChecked: boolean) => void;
  onRenameItem: (itemId: string, title: string) => void;
  onDeleteItem: (itemId: string) => void;
  onConvertItemToTask: (itemId: string) => void;
  onConvertItemToSubtask: (itemId: string) => void;
}

export function ChecklistCard({
  checklist,
  canConvertToSubtask,
  onAddItem,
  onRenameChecklist,
  onDeleteChecklist,
  onToggleItem,
  onRenameItem,
  onDeleteItem,
  onConvertItemToTask,
  onConvertItemToSubtask,
}: ChecklistCardProps) {
  const { t } = useTranslation('checklists');
  const [collapsed, setCollapsed] = useState(false);
  const [addingItem, setAddingItem] = useState(false);
  const [itemDraft, setItemDraft] = useState('');
  const dropRef = useRef<HTMLDivElement>(null);

  const progress =
    checklist.totalItems === 0
      ? 0
      : Math.round((checklist.checkedItems / checklist.totalItems) * 100);

  useEffect(() => {
    const el = dropRef.current;
    if (!el) return;
    return dropTargetForElements({
      element: el,
      getData: () => ({ checklistId: checklist.id }),
    });
  }, [checklist.id]);

  function commitNewItem() {
    const parsed = createChecklistItemTitleSchema(t).safeParse(itemDraft);
    if (parsed.success) {
      onAddItem(checklist.id, parsed.data);
      setItemDraft('');
    }
  }

  return (
    <div data-testid="checklists-card">
      <ChecklistCardHeader
        checklist={checklist}
        collapsed={collapsed}
        progress={progress}
        onToggleCollapsed={() => setCollapsed((c) => !c)}
        onRename={(name) => onRenameChecklist(checklist.id, name)}
        onDelete={() => onDeleteChecklist(checklist)}
      />

      {!collapsed && (
        <div className="pl-5">
          <div
            ref={dropRef}
            className={cn(
              'divide-y divide-border/50 overflow-hidden rounded-md border border-border/50',
              checklist.items.length === 0 && 'min-h-2',
            )}
          >
            {checklist.items
              .slice()
              .sort((a, b) => a.order - b.order)
              .map((item) => (
                <ChecklistItemRow
                  key={item.id}
                  item={item}
                  canConvertToSubtask={canConvertToSubtask}
                  onToggle={onToggleItem}
                  onRename={onRenameItem}
                  onDelete={onDeleteItem}
                  onConvertToTask={onConvertItemToTask}
                  onConvertToSubtask={onConvertItemToSubtask}
                />
              ))}

            {addingItem ? (
              <div className="flex items-center gap-2 px-2 py-1.5">
                <Plus className="size-3.5 shrink-0 text-muted-foreground" />
                <Input
                  autoFocus
                  value={itemDraft}
                  onChange={(e) => setItemDraft(e.target.value)}
                  onBlur={() => {
                    if (itemDraft.trim()) commitNewItem();
                    setAddingItem(false);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') commitNewItem();
                    else if (e.key === 'Escape') {
                      setItemDraft('');
                      setAddingItem(false);
                    }
                  }}
                  placeholder={t('item.addPlaceholder')}
                  aria-label={t('checklist.addItem')}
                  className="h-7 flex-1 border-none text-sm shadow-none focus-visible:ring-0"
                  data-testid="checklists-add-item-input"
                />
              </div>
            ) : (
              <button
                type="button"
                className="flex w-full items-center gap-2 px-2 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-accent/30 hover:text-foreground"
                onClick={() => setAddingItem(true)}
                data-testid="checklists-add-item-btn"
              >
                <Plus className="size-3" />
                {t('checklist.addItem')}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
